import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../services/api';

const LIVE_MATCHES_CACHE_KEY = 'tahminarena-live-matches-cache';
const LIVE_MATCHES_FALLBACK_KEY = 'tahminarena-live-matches-fallback';
const LIVE_INSIGHTS_CACHE_KEY = 'tahminarena-live-insights-cache';
const LIVE_CACHE_TTL_MS = 45 * 1000;
const LIVE_INSIGHT_TTL_MS = 2 * 60 * 1000;
const LIVE_ZERO_RESULT_GRACE_MS = 10 * 1000;
const LIVE_DEFAULT_RETRY_MS = 30 * 1000;
const LIVE_TEMP_RETRY_MS = 60 * 1000;
const LIVE_MATCHES_SOFT_MESSAGE = 'Canli mac verisi su anda alinamadi. Sistem kisa sure icinde tekrar deneyecek.';
const LIVE_TEMPORARY_MESSAGE = 'Canli servis gecici olarak cevap vermedi, otomatik tekrar denenecek.';

const readLiveCache = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.sessionStorage.getItem(LIVE_MATCHES_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp || !Array.isArray(parsed?.matches)) return [];
    if ((Date.now() - Number(parsed.timestamp)) > LIVE_CACHE_TTL_MS) return [];
    return parsed.matches;
  } catch (error) {
    return [];
  }
};

const writeLiveCache = (matches) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      LIVE_MATCHES_CACHE_KEY,
      JSON.stringify({
        timestamp: Date.now(),
        matches
      })
    );
  } catch (error) {
    // ignore cache write issues
  }
};

const readLiveFallback = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LIVE_MATCHES_FALLBACK_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp || !Array.isArray(parsed?.matches)) return [];
    if ((Date.now() - Number(parsed.timestamp)) > LIVE_ZERO_RESULT_GRACE_MS) return [];
    return parsed.matches;
  } catch (error) {
    return [];
  }
};

const writeLiveFallback = (matches) => {
  if (typeof window === 'undefined' || !Array.isArray(matches) || matches.length === 0) return;
  try {
    window.localStorage.setItem(
      LIVE_MATCHES_FALLBACK_KEY,
      JSON.stringify({
        timestamp: Date.now(),
        matches
      })
    );
  } catch (error) {
    // ignore cache write issues
  }
};

const readLastNonEmptyLiveCache = () => {
  const cachedMatches = readLiveCache();
  if (Array.isArray(cachedMatches) && cachedMatches.length) return cachedMatches;
  return readLiveFallback();
};

const isTemporaryLiveIssue = (payload) => {
  const errorCode = String(payload?.errorCode || '').trim();
  const providerStatus = Number(payload?.providerStatus ?? payload?.status ?? 0);

  return Boolean(
    payload?.temporary ||
    ['LIVE_PROVIDER_TEMP_ERROR', 'LIVE_API_NON_JSON_RESPONSE', 'LIVE_API_REQUEST_FAILED', 'LIVE_API_FETCH_FAILED'].includes(errorCode) ||
    [502, 503, 504].includes(providerStatus)
  );
};

const readInsightCache = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(LIVE_INSIGHTS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeInsightCache = (cache) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LIVE_INSIGHTS_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    // ignore cache write issues
  }
};

const getInsightScore = (match) =>
  Number(match?.predictionContext?.liveInsightScore || match?.liveInsights?.highlights?.liveConfidenceScore || 0);

const hasLiveInsights = (match) => Boolean(match?.liveInsights?.insights?.length);

const mergeLiveInsightsWithCache = (matches) => {
  const cache = readInsightCache();
  const now = Date.now();

  const mergedMatches = matches.map((match) => {
    const fixtureId = String(match.fixtureId || match.id || match._id);
    const currentScore = getInsightScore(match);
    const cached = cache[fixtureId];
    const cachedIsFresh = cached?.timestamp && (now - cached.timestamp) <= LIVE_INSIGHT_TTL_MS;

    if (hasLiveInsights(match) && currentScore >= 70) {
      cache[fixtureId] = {
        timestamp: now,
        score: currentScore,
        liveInsights: match.liveInsights
      };
      return match;
    }

    if (
      cachedIsFresh &&
      cached?.liveInsights &&
      (
        !hasLiveInsights(match) ||
        currentScore < 70 ||
        (Number(cached.score || 0) >= 75 && Number(cached.score || 0) > currentScore + 8)
      )
    ) {
      return {
        ...match,
        liveInsights: cached.liveInsights,
        predictionContext: {
          ...(match.predictionContext || {}),
          liveInsights: cached.liveInsights,
          liveInsightScore: cached.score
        }
      };
    }

    if (!cachedIsFresh && cache[fixtureId]) {
      delete cache[fixtureId];
    }

    return match;
  });

  writeInsightCache(cache);
  return mergedMatches;
};

export const useMatches = (favoriteFixtureIds = []) => {
  const initialLiveMatches = readLastNonEmptyLiveCache();
  const [liveMatches, setLiveMatches] = useState(() => initialLiveMatches);
  const [todayMatches, setTodayMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(() => initialLiveMatches.length === 0);
  const [refreshingLive, setRefreshingLive] = useState(false);
  const [error, setError] = useState(null);
  const [liveNotice, setLiveNotice] = useState('');
  const [liveWarning, setLiveWarning] = useState('');
  const [liveDebug, setLiveDebug] = useState(null);
  const lastLiveSuccessAtRef = useRef(initialLiveMatches.length ? Date.now() : 0);
  const retryDelayRef = useRef(LIVE_DEFAULT_RETRY_MS);
  const pollingTimeoutRef = useRef(null);
  const favoriteIdsKey = useMemo(() => JSON.stringify(favoriteFixtureIds), [favoriteFixtureIds]);
  const normalizedFavoriteFixtureIds = useMemo(() => {
    try {
      const parsed = JSON.parse(favoriteIdsKey);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }, [favoriteIdsKey]);

  const fetchLiveMatches = useCallback(async (showLoader = false) => {
    try {
      setError(null);
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshingLive(true);
      }
      const data = await api.getLiveMatches(false, 50, normalizedFavoriteFixtureIds);
      const temporaryResponse = isTemporaryLiveIssue(data);
      const responseMatches = Array.isArray(data?.matches) ? data.matches : [];
      const localFallbackMatches = responseMatches.length === 0 ? readLastNonEmptyLiveCache() : [];
      const nextSourceMatches = responseMatches.length === 0 && localFallbackMatches.length > 0
        ? localFallbackMatches
        : responseMatches;
      const stabilizedMatches = mergeLiveInsightsWithCache(nextSourceMatches);
      const usingLocalFallback = responseMatches.length === 0 && localFallbackMatches.length > 0;

      retryDelayRef.current = temporaryResponse ? LIVE_TEMP_RETRY_MS : LIVE_DEFAULT_RETRY_MS;
      setLiveNotice(
        temporaryResponse
          ? (data?.message || LIVE_TEMPORARY_MESSAGE)
          : (data?.success === false ? (data?.message || LIVE_MATCHES_SOFT_MESSAGE) : '')
      );
      setLiveWarning(
        usingLocalFallback
          ? 'Canli veri kisa sureli olarak cache uzerinden gosteriliyor.'
          : (temporaryResponse ? LIVE_TEMPORARY_MESSAGE : (data?.warning || ''))
      );
      setLiveDebug(
        data?.debug && typeof data.debug === 'object'
          ? {
            ...data.debug,
            errorCode: data?.errorCode || data?.debug?.errorCode || null,
            temporary: temporaryResponse
          }
          : (temporaryResponse
            ? {
              errorCode: data?.errorCode || 'LIVE_PROVIDER_TEMP_ERROR',
              providerStatus: data?.providerStatus ?? null,
              preview: data?.preview || null,
              temporary: true
            }
            : null)
      );

      setLiveMatches((current) => {
        const shouldPreserveCurrent =
          !showLoader &&
          Array.isArray(current) &&
          current.length > 0 &&
          stabilizedMatches.length === 0 &&
          lastLiveSuccessAtRef.current > 0 &&
          (Date.now() - lastLiveSuccessAtRef.current) <= LIVE_ZERO_RESULT_GRACE_MS;

        const nextMatches = shouldPreserveCurrent ? current : stabilizedMatches;
        writeLiveCache(nextMatches);
        if (nextMatches.length > 0) {
          writeLiveFallback(nextMatches);
        }
        return nextMatches;
      });
      if (stabilizedMatches.length > 0) {
        lastLiveSuccessAtRef.current = Date.now();
      }
    } catch (err) {
      const temporaryError = isTemporaryLiveIssue(err);
      const fallbackMatches = mergeLiveInsightsWithCache(readLastNonEmptyLiveCache());
      if (fallbackMatches.length > 0) {
        setLiveMatches(fallbackMatches);
        writeLiveCache(fallbackMatches);
        writeLiveFallback(fallbackMatches);
        lastLiveSuccessAtRef.current = Date.now();
      }

      retryDelayRef.current = temporaryError ? LIVE_TEMP_RETRY_MS : LIVE_DEFAULT_RETRY_MS;
      setLiveNotice(temporaryError ? LIVE_TEMPORARY_MESSAGE : (err?.message || LIVE_MATCHES_SOFT_MESSAGE));
      setLiveWarning(
        fallbackMatches.length > 0
          ? 'Canli veri kisa sureli olarak son basarili cache uzerinden gosteriliyor.'
          : (temporaryError ? LIVE_TEMPORARY_MESSAGE : '')
      );
      setLiveDebug({
        errorCode: err?.errorCode || 'LIVE_API_FETCH_FAILED',
        providerStatus: err?.providerStatus ?? null,
        preview: err?.preview || null,
        temporary: temporaryError
      });
    } finally {
      if (showLoader) {
        setLoading(false);
      }
      setRefreshingLive(false);
    }
  }, [normalizedFavoriteFixtureIds]);

  const fetchTodayMatches = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      const data = await api.getTodayMatches(false, 200);
      setTodayMatches(data.matches);
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  const fetchUpcomingMatches = useCallback(async () => {
    try {
      const data = await api.getUpcomingMatches(8);
      setUpcomingMatches(data.matches);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const runLivePolling = async (showLoader = false) => {
      await fetchLiveMatches(showLoader);

      if (cancelled) return;

      pollingTimeoutRef.current = window.setTimeout(() => {
        runLivePolling(false);
      }, retryDelayRef.current);
    };

    fetchTodayMatches(false);
    fetchUpcomingMatches();
    runLivePolling(true);

    return () => {
      cancelled = true;
      if (pollingTimeoutRef.current) {
        window.clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [fetchLiveMatches, fetchTodayMatches, fetchUpcomingMatches]);

  return { 
    liveMatches, 
    todayMatches, 
    upcomingMatches,
    loading, 
    refreshingLive,
    error, 
    liveNotice,
    liveWarning,
    liveDebug,
    refetch: () => Promise.all([fetchLiveMatches(false), fetchTodayMatches(false), fetchUpcomingMatches()])
  };
};
